import { colors } from "constants/tailwind";
import { roundRating } from "lib/numberUtils";
import { isPrimaryContribution } from "lib/contributions";
import { generateFeaturedBookSeries } from "lib/bookUtils";
import StarIcon from "icons/solid/star.svg";
import HalfStarIcon from "icons/solid/star-half-stroke.svg";
import {
  AUTHOR_AVATAR_URL,
  DEFAULT_COVER_URL,
  HARDCOVER_LOGO_URL,
  normalizeImageUrl,
} from "lib/imageTools";
import { usePage } from "@inertiajs/react";
import { BookOgImageProps } from "./index.props";
import Icon from "hardcover-ui/components/Icon";

const DESCRIPTION_LENGTH = 120;

function calculateTitleSize(characters) {
  if (characters < 15) {
    return "text-5xl";
  }
  if (characters < 25) {
    return "text-4xl";
  }
  if (characters < 35) {
    return "text-3xl";
  }
  return "text-2xl";
}

function BookOgImage() {
  const { book } = usePage<BookOgImageProps>().props;

  // Title
  const titleSize = calculateTitleSize(book.title.length);
  const title = book.releaseYear
    ? `${book.title} (${book.releaseYear})`
    : book.title;

  // Series
  const featuredSeries = generateFeaturedBookSeries(book);

  // Description
  const description =
    book.description?.length > DESCRIPTION_LENGTH
      ? `${book.description.slice(0, DESCRIPTION_LENGTH)}...`
      : book.description;

  // Rating
  const stars = Math.round(book.rating);
  const addHalfStar = book.rating > stars;

  // Genres
  const genres = (book.genres || []).slice(0, 3).map((genre) => genre.tag);

  // Authors
  const primaryContributions = book.contributions.filter((contribution) =>
    isPrimaryContribution(contribution)
  );
  const authors = primaryContributions.map((contribution) => {
    const url = normalizeImageUrl(
      contribution.author.image?.url || AUTHOR_AVATAR_URL
    );
    return {
      slug: contribution.author.slug,
      name: contribution.author.name,
      imageUrl: url,
    };
  });

  // Cover
  const coverUrl = normalizeImageUrl(book.image?.url || DEFAULT_COVER_URL);
  const accentColor = book.image.color || colors.gray[800];

  return (
    <div
      className="grid grid-cols-2"
      style={{
        height: "630px",
        width: "1200px",
      }}
    >
      <div className="flex flex-col gap-2 h-full justify-center bg-gray-50 p-12">
        <div className="flex flex-wrap items-baseline">
          <h1 className={`font-serif text-gray-900 ${titleSize}`}>{title}</h1>
        </div>
        {featuredSeries && (
          <div className="flex text-2xl text-gray-700 font-semibold">
            <div className="flex rounded-full bg-gray-200 py-1 px-3 ">
              {featuredSeries}
            </div>
          </div>
        )}
        <div className="mt-2 flex items-center w-full text-xl flex-wrap">
          <span className="text-gray-400 font-medium mr-2">By</span>
          {authors.map((author, index) => (
            <span key={author.slug} className="flex items-center flex-wrap">
              {authors.length === 1 && (
                <span className="flex w-8 h-8 rounded-full overflow-hidden mr-1">
                  <img src={author.imageUrl} alt="Author Name" />
                </span>
              )}
              <span
                className="text-gray-700 font-medium"
                style={{ wordBreak: "break-all" }}
              >
                {author.name}
              </span>
              {authors.length > 0 && authors.length - 1 !== index && <>, </>}
            </span>
          ))}
        </div>

        <p
          className="text-2xl font-medium text-gray-500"
          style={{ wordWrap: "break-word" }}
        >
          {description}
        </p>

        {genres.length > 0 && (
          <div className="flex items-center mt-2">
            {genres.map((genre) => (
              <div
                key={genre}
                className="flex items-center py-1 px-3 rounded-full bg-gray-200 text-2xl text-gray-700 font-semibold mr-2"
              >
                {genre}
              </div>
            ))}
          </div>
        )}

        <div className="flex text-2xl font-medium text-gray-500 mt-4 items-center">
          <span>Average rating:</span>
          <span className="text-gray-700 pl-1 pr-2">
            {roundRating(book.rating, 2)}
          </span>
          <span className="pl-2 flex flex-row">
            {[...Array(stars)].map((i, index) => (
              <Icon icon={StarIcon} key={`star-${i}-${index}`}
                style={{
                  width: "30px",
                  height: "24px",
                  color: colors.yellow[500],
                }}
              />
            ))}
            {addHalfStar && (
              <Icon icon={HalfStarIcon}
                style={{
                  width: "30px",
                  height: "24px",
                  color: colors.yellow[500],
                }}
              />
            )}
          </span>
        </div>
      </div>

      <div
        className="col-span-1 flex relative"
        style={{
          backgroundImage: `linear-gradient(to bottom, ${accentColor}, ${colors.gray[800]})`,
        }}
      >
        <div className="absolute flex justify-center items-center w-full h-full">
          <img
            src={coverUrl}
            alt="cover"
            className="w-64 rounded-full shadow-lg mb-24 rounded-r-xl rounded-l-lg"
          />
        </div>

        <div className="absolute top-0 bottom-0 flex items-end justify-center w-full h-full p-8">
          <img src={HARDCOVER_LOGO_URL} alt="Hardcover" width={350} />
        </div>
      </div>
    </div>
  );
}

// BookOgImage.layout = FocusedLayout;
export default BookOgImage;
